/**
 * Where a project or a dataspace lives — personal, or in one department — and what is said about
 * it wherever it is shown, picked, moved or mapped: the Designer, the Operator and the control
 * center all use these.
 *
 * `errors` is keyed by the server's error codes and looked up dynamically
 * (`utils/spaceErrors.ts`), so those key names are a contract with the API rather than free-form.
 */
export default {
	spaces: {
		personal: "Personal",
		kind: {
			project: "project",
			dataspace: "dataspace",
		},
		picker: {
			label: "Location",
			help: "Personal: only the people you add reach it. In a department: its members reach it through the roles the department maps.",
		},
		filter: {
			label: "Department",
			all: "All",
			personal: "Personal",
		},
		location: {
			title: "Department",
			personal: {
				project:
					"This project is personal: only the people added to it reach it. Move it into a department to give that department's members access.",
				dataspace:
					"This dataspace is personal: only the people added to it reach it. Move it into a department to give that department's members access.",
			},
			inDepartment: {
				project:
					"This project belongs to {department}. Its members reach it through the department access, and the department's owners administer it.",
				dataspace:
					"This dataspace belongs to {department}. Its members reach it through the department access, and the department's owners administer it.",
			},
			needsDelete: {
				project:
					"Moving a project means giving it away, which takes the right to delete it. You do not have that right.",
				dataspace:
					"Moving a dataspace means giving it away, which takes the right to delete it. You do not have that right.",
			},
			cannotLeave:
				"Taking a space out of {department} takes the right to manage {department}'s spaces, which your role there does not include.",
			nowhereToGo:
				"You are in no department whose spaces you may move this into. Moving in takes the right to move spaces into a department.",
			moveTo: "Move to department…",
			makePersonal: "Make personal",
		},
		move: {
			titleTo: "Move {name} to a department",
			titlePersonal: "Make {name} personal",
			consequenceIn:
				"Its department access is reset: the department's owners become its admins, and nobody else reaches it through the department until you map more roles. People added to it by hand keep their access.",
			consequenceOut:
				"Nobody reaches it through the department any more, and its department access is deleted. If no one added to it by hand is an admin, you become its admin.",
			target: "Department",
			pick: "Pick a department",
			none: "You are in no department you may move spaces into.",
			confirmTo: "Move",
			confirmPersonal: "Make personal",
			movedTo: "{name} is now in {department}.",
			madePersonal: "{name} is personal now.",
		},
		access: {
			title: "Department access",
			intro: {
				project:
					"The members of {department} reach this project through the role their department role is mapped to. People added to it by hand keep the role they were given.",
				dataspace:
					"The members of {department} reach this dataspace through the role their department role is mapped to. People added to it by hand keep the role they were given.",
			},
			departmentRole: "Department role",
			gets: "Role in the {kind}",
			everyone: "Every member",
			locked: "Fixed row",
			lockedHint:
				"The department's owners always administer what the department holds, so this row cannot be changed or removed.",
			add: "Add mapping",
			remove: "Remove the mapping for {role}",
			empty: "Only the department's owners reach this through the department.",
			saved: "Department access saved.",
			changedElsewhere:
				"Someone else changed this department access while you were editing it. Your edits are kept; discard them to see theirs.",
			readOnly: "You may read this department access, not change it.",
		},
		addFromDepartment: {
			action: "Add from department",
			title: "Add from {department}",
			note: {
				project:
					"Members of {department} who are not on this project yet. Only members of {department} can be on it: anyone else joins the department first.",
				dataspace:
					"Members of {department} who are not on this dataspace yet. Only members of {department} can be on it: anyone else joins the department first.",
			},
			search: "Search by name or email…",
			label: "Members of {department}",
			empty: {
				project: "Every member of {department} is on this project already.",
				dataspace: "Every member of {department} is on this dataspace already.",
			},
			role: "Role",
			submit: "Add | Add | Add {n} people",
			added: "{name} was added. | {n} people were added.",
		},
		errors: {
			mapping_locked:
				"The department's owners always get the admin role, so that row cannot be changed.",
			not_in_department:
				"A space is not in the department it was expected to be in. It may have been moved just now: reload and try again.",
			role_in_use:
				"A department's access still gives this role to people. Remove or change that mapping first.",
			space_needs_admin:
				"A space would be left with no admin of its own once it is personal. Add an admin to it first, from its page in the control center.",
			invalid_role:
				"That is no longer a role of the department or of the space. Reload and try again.",
			duplicate_mapping: "A department role can be mapped to one role of the space only.",
			no_spaces: "Pick at least one project or dataspace.",
			role_required: "The default department needs the role it gives everyone.",
			space_not_found:
				"A space is not there, or you cannot reach it any more. Nothing was moved.",
			missing_entitlement:
				"Your role does not allow this. Moving a space takes the right to delete it and the matching right in the departments involved; creating one takes the right to create in the department.",
			invitation_required:
				"Only a member of the department can be added from this list. Anyone else is invited to the department first.",
			not_a_department_member:
				"Only members of the space's department can be on it. They join the department first.",
		},
		/** A space's member list: everybody who reaches it, and how. */
		members: {
			viaDepartment: "through {department}",
			alsoViaDepartment: "also {roles} through {department}",
			notInDepartment: "not a member of {department}",
			noAccess: "No access",
			noAccessBand: "Not in the department",
			removeKeepsDepartment:
				"Their own membership is removed. They keep what {department} gives them here.",
		},
	},
};
